import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, ImageIcon, Info, Loader2, Pencil, RefreshCw, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatTemplateName } from '@/lib/templateName';
import { normalize, selectedPhotos, selectedText } from '@/lib/generatedContent';
import type { CampaignLaunchState } from '@/hooks/useCampaignLaunchState';
import { useMediaErrorRefresh } from '@/hooks/useMediaErrorRefresh';
import { StepFooter, StepHeader } from './StepLayout';
import { HeaderMediaFields, SmsMessageOverride } from './TemplateFields';

interface MediaOption {
  id: string;
  name: string;
  type?: string;
  createdAt?: string;
  generatedContent?: unknown;
}

interface MediaStepProps<C extends { id: string; campaignMediaId: string | null }> {
  workspaceId: string;
  campaign: C;
  state: CampaignLaunchState;
  onBack: () => void;
  onNext: () => void;
  /** Back to the channel step, from the "choose a template first" notice. */
  onChooseTemplate: () => void;
}

/**
 * Step 3: the creative to send, and what of it goes into the message.
 *
 * A campaign cannot be set up past here until it has one: the template's media header comes from it,
 * and for SMS the body text does. Cards rather than a dropdown of names, because the thing being
 * chosen is a picture and some copy, and a name alone is a poor way to recognise either. Below the
 * cards sits whatever the channel step's choice asks of the media: a WhatsApp template's header
 * image, or the SMS text override.
 */
export default function MediaStep<C extends { id: string; campaignMediaId: string | null }>({
  workspaceId, campaign, state, onBack, onNext, onChooseTemplate,
}: MediaStepProps<C>) {
  const { t } = useTranslation();
  const [media, setMedia] = useState<MediaOption[]>([]);
  const [loading, setLoading] = useState(true);
  // A FAILED fetch is tracked separately from an empty one. Collapsing the two rendered "You have
  // no media yet — Create media" whenever the request merely failed, which sends somebody off to
  // generate creative they already have.
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [choosing, setChoosing] = useState<string | null>(null);

  // `quiet` is the refetch for thumbnails whose signed URLs expired: the cards stay as they are, and
  // a failure leaves them so rather than replacing them with the error state.
  const loadMedia = useCallback(async (term: string, { quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
      setLoadError(false);
    }
    try {
      const params = term ? `?search=${encodeURIComponent(term)}` : '';
      const data = await apiFetch<{ media: MediaOption[] }>(`/workspaces/${workspaceId}/campaign-media${params}`);
      setMedia(data.media || []);
    } catch (error) {
      console.warn('[Campaign] could not load media options:', error);
      if (!quiet) {
        setMedia([]);
        setLoadError(true);
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [workspaceId]);
  const refreshThumbnails = useMediaErrorRefresh(() => loadMedia(search, { quiet: true }));

  useEffect(() => {
    if (!workspaceId) return;
    const debounce = setTimeout(() => loadMedia(search), search ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [workspaceId, search, loadMedia]);

  // Through the launch state rather than a PATCH of its own: choosing a template can switch the
  // media too, and the launch state runs those switches one at a time — two racing would leave the
  // server holding one media and the screen showing another.
  const chooseMedia = async (mediaId: string) => {
    if (mediaId === campaign.campaignMediaId || choosing || state.switchingMedia) return;
    setChoosing(mediaId);
    const switched = await state.switchCampaignMedia(mediaId);
    setChoosing(null);
    if (switched) toast.success(t('campaigns.media_step.updated', 'Media selected'));
  };

  const hasMedia = Boolean(campaign.campaignMediaId);
  const busy = Boolean(choosing) || state.switchingMedia;
  const templateName = formatTemplateName(state.selectedTemplate?.name);
  // Named when it is in the list on screen; a search can hide it, and then the sentence says less.
  const suggestedMediaName = state.templateMediaSuggestion
    ? media.find((item) => item.id === state.templateMediaSuggestion)?.name
    : undefined;

  return (
    <Card className="p-6">
      <StepHeader step="media" />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('campaigns.media_step.search', 'Search media...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-10"
            aria-label={t('campaigns.media_step.search', 'Search media...')}
          />
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/w/${workspaceId}/campaigns/media`}>
            <Pencil className="me-2 h-3.5 w-3.5" /> {t('campaigns.media_step.library', 'Open library')}
          </Link>
        </Button>
      </div>

      {/* Where the preselection came from, so a card already ticked is not a mystery... */}
      {state.templateMediaApplied && (
        <p className="mb-4 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground" dir="auto">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
          {t(
            'campaigns.media_step.from_template',
            'Pre-selected from the template “{{name}}”. Pick other media below to use it for this campaign only.',
            { name: templateName },
          )}
        </p>
      )}
      {/* ...and when somebody chose other media themselves, the template's is offered, never forced. */}
      {state.templateMediaSuggestion && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm">
          <p className="flex items-start gap-2 text-muted-foreground" dir="auto">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
            {suggestedMediaName
              ? t('campaigns.media_step.template_made_from', 'The template “{{template}}” was made from “{{media}}”.', {
                template: templateName, media: suggestedMediaName,
              })
              : t('campaigns.media_step.template_made_from_other', 'The template “{{template}}” was made from other media.', {
                template: templateName,
              })}
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => chooseMedia(state.templateMediaSuggestion!)}
          >
            {t('campaigns.media_step.use_template_media', 'Use it')}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="me-2 h-5 w-5 animate-spin" /> {t('campaigns.media_step.loading', 'Loading media…')}
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 opacity-50" />
          <p>{t('campaigns.media_step.error', "Couldn't load your media library.")}</p>
          <Button variant="outline" onClick={() => loadMedia(search)}>
            <RefreshCw className="me-2 h-4 w-4" /> {t('campaigns.media_step.retry', 'Retry')}
          </Button>
        </div>
      ) : media.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
          <ImageIcon className="h-10 w-10 opacity-50" />
          <p>
            {search
              ? t('campaigns.media_step.no_match', 'No media matches that search.')
              : t('campaigns.media_step.empty', 'You have no media yet.')}
          </p>
          {!search && (
            <Button asChild>
              <Link to={`/w/${workspaceId}/campaigns/media/new`}>
                <Sparkles className="me-2 h-4 w-4" /> {t('campaigns.media_step.create', 'Create media')}
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <div role="radiogroup" aria-label={t('campaigns.steps.media', 'Choose media')} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {media.map((item) => (
            <MediaChoiceCard
              key={item.id}
              item={item}
              selected={item.id === campaign.campaignMediaId}
              busy={choosing === item.id}
              disabled={busy}
              fromTemplate={item.id === state.templateMediaId}
              onSelect={() => chooseMedia(item.id)}
              onMediaError={refreshThumbnails}
            />
          ))}
        </div>
      )}

      {hasMedia && (
        <div className="mt-6 border-t pt-6">
          <MessageMediaFields state={state} onChooseTemplate={onChooseTemplate} />
        </div>
      )}

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!state.mediaValid || busy}
        hint={!hasMedia
          ? t('campaigns.media_step.required', 'Choose a media item to continue.')
          : !state.mediaValid ? state.mediaBlockers[0] : undefined}
      />
    </Card>
  );
}

function MessageMediaFields({ state, onChooseTemplate }: { state: CampaignLaunchState; onChooseTemplate: () => void }) {
  const { t } = useTranslation();

  if (state.channel !== 'whatsapp') return <SmsMessageOverride state={state} />;

  // Reached by a link or a reload rather than through the channel step: without a template there is
  // no header format to choose media for.
  if (!state.whatsappTemplateId) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
        <p className="flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {t('campaigns.media_step.template_first', 'Choose a WhatsApp template first — it decides whether a header image is needed.')}
        </p>
        <Button size="sm" variant="outline" onClick={onChooseTemplate}>
          {t('campaigns.steps.channel', 'WhatsApp template')}
        </Button>
      </div>
    );
  }

  if (state.loadingRequirements) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking what this template needs...
      </div>
    );
  }

  if (state.needsMediaHeader && !state.requirements?.hasCarousel) return <HeaderMediaFields state={state} />;

  return (
    <div className="flex gap-3 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
      <Info className="h-4 w-4 shrink-0 mt-0.5" />
      <p>
        {t(
          'campaigns.media_step.no_header',
          "The template you chose has no media header, so no image is sent with it. The media is kept with the campaign as its creative.",
        )}
      </p>
    </div>
  );
}

interface MediaChoiceCardProps {
  item: MediaOption;
  selected: boolean;
  busy: boolean;
  disabled: boolean;
  /** The media the chosen template was made from — its default creative. */
  fromTemplate: boolean;
  onSelect: () => void;
  /** The thumbnail failed to load — most likely its signed URL expired. */
  onMediaError: () => void;
}

function MediaChoiceCard({ item, selected, busy, disabled, fromTemplate, onSelect, onMediaError }: MediaChoiceCardProps) {
  const { t } = useTranslation();
  // The FINAL selection, which is exactly what the campaign gets when it picks this media — the
  // same reading the media library's cards use, so the two show the same picture.
  const gc = useMemo(() => normalize(item.generatedContent), [item.generatedContent]);
  const text = selectedText(gc)?.content || '';
  const photo = selectedPhotos(gc)[0];

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled && !busy}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border bg-card text-start transition-all',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
        selected ? 'border-primary ring-2 ring-primary' : 'hover:border-primary/60 hover:shadow-sm',
        disabled && !busy && 'opacity-60',
      )}
    >
      {photo?.url ? (
        <img src={photo.url} alt="" className="aspect-video w-full object-cover" onError={onMediaError} />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center bg-muted text-muted-foreground">
          <ImageIcon className="h-8 w-8 opacity-40" />
        </div>
      )}

      {(selected || busy) && (
        <span className="absolute end-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        </span>
      )}
      {fromTemplate && (
        <span className="absolute start-2 top-2 rounded bg-background/90 px-1.5 py-0.5 text-[11px] font-medium shadow">
          {t('campaigns.media_step.template_badge', 'Template media')}
        </span>
      )}

      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <span className="font-semibold leading-tight line-clamp-2">{item.name}</span>
        <span className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap" dir="auto">
          {text || <span className="italic">No content generated yet</span>}
        </span>
      </div>
    </button>
  );
}
